import { ContextType } from '@carvana/eventable-clickstream';
import { LocationSource } from '@carvana/merch-geolocation/api/constants';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import type {
  DeliveryApi,
  Errors,
  GeoApi,
  LocationChangeCallback,
  LocationContextType,
  LocationData,
  State
} from '~/contracts/common/Geolocation';
import hasValidLocation from '~/utilities/geolocation/hasValidLocation';
import maybeIsExcludedLocation from '~/utilities/geolocation/maybeIsExcludedLocation';

const defaultErrorState: Errors = {
  deliveryError: false,
  locationError: false
};

const defaultState: State = {
  city: '',
  pickUpInfoToDisplay: null,
  state: '',
  zip: ''
};

const defaultContext: LocationContextType = {
  ...defaultState,
  errorState: defaultErrorState,
  isExcludedLocation: false,
  onCloseHandler: () => {},
  setGeolocation: (_zipcode: string) =>
    new Promise<LocationData>(resolve =>
      resolve({
        city: '',
        source: undefined,
        state: '',
        zip: ''
      })
    ),
  trackEvent() {}
};

export const LocationContext =
  createContext<LocationContextType>(defaultContext);

export const useLocationContext = () => useContext(LocationContext);

type Props = {
  children: React.ReactNode;
  currentZip: string;
  fetchDelivery: DeliveryApi;
  geoApi: GeoApi;
  isOpen?: boolean;
  onCloseHandler: () => void;
  onUserLocationChange?: LocationChangeCallback;
  trackEvent: ContextType['trackEvent'];
};

export default function LocationContextProvider({
  children,
  currentZip,
  fetchDelivery,
  geoApi,
  isOpen,
  onCloseHandler,
  onUserLocationChange,
  trackEvent
}: Props) {
  const [location, setLocation] = useState<State>(defaultState);
  const [errorState, setErrorState] = useState<Errors>(defaultErrorState);

  useEffect(() => {
    setErrorState(defaultErrorState);
  }, [isOpen]);

  const setGeolocation = useCallback(
    async (zipcode: string) => {
      const locationToSave: State = {
        city: '',
        state: '',
        zip: '',
        pickUpInfoToDisplay: null
      };
      const errors: Errors = {
        locationError: false,
        deliveryError: false
      };

      try {
        const res = await geoApi.setGeoLocation({
          zip: zipcode,
          source: LocationSource.Modal
        });
        if (res.source === LocationSource.Default) {
          errors.locationError = true;
        } else {
          locationToSave.city = res.city;
          locationToSave.state = res.state;
          locationToSave.zip = res.zip;
        }
      } catch (err) {
        errors.locationError = true;
      }

      try {
        if (!errors.locationError) {
          const { pickUpInfoToDisplay, isDeliveryAllowed } =
            await fetchDelivery(zipcode);
          locationToSave.pickUpInfoToDisplay = pickUpInfoToDisplay;
          locationToSave.isDeliveryAllowed = isDeliveryAllowed;
        }
      } catch (err) {
        errors.deliveryError = true;
      }

      setErrorState(errors);

      if (!hasValidLocation(locationToSave)) {
        throw new Error('Location is invalid');
      }

      if (onUserLocationChange) {
        onUserLocationChange({
          location: locationToSave,
          errorState: errors
        });
      }

      setLocation(locationToSave);

      return locationToSave;
    },
    [fetchDelivery, geoApi, onUserLocationChange]
  );

  useEffect(() => {
    (async () => {
      const locationToSave: State = {
        city: '',
        state: '',
        zip: '',
        pickUpInfoToDisplay: null
      };
      const errors: Errors = {
        locationError: false,
        deliveryError: false
      };

      try {
        const res = await geoApi.getGeoLocation();
        locationToSave.city = res.city;
        locationToSave.state = res.state;
        locationToSave.zip = res.zip;
      } catch (err) {
        errors.locationError = true;
      }

      try {
        if (!errors.locationError) {
          const { pickUpInfoToDisplay, isDeliveryAllowed } =
            await fetchDelivery(locationToSave.zip);
          locationToSave.pickUpInfoToDisplay = pickUpInfoToDisplay;
          locationToSave.isDeliveryAllowed = isDeliveryAllowed;
        }
      } catch (err) {
        errors.deliveryError = true;
      }

      if (onUserLocationChange) {
        onUserLocationChange({
          location: locationToSave,
          errorState: errors
        });
      }

      setErrorState(errors);
      setLocation(locationToSave);
    })();
  }, [currentZip, fetchDelivery, geoApi, onUserLocationChange]);

  const isExcludedLocation = maybeIsExcludedLocation(location);

  const value = useMemo<LocationContextType>(
    () => ({
      ...location,
      errorState,
      isExcludedLocation,
      onCloseHandler,
      setGeolocation,
      trackEvent
    }),
    [
      errorState,
      isExcludedLocation,
      location,
      onCloseHandler,
      setGeolocation,
      trackEvent
    ]
  );

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
}

LocationContextProvider.displayName = 'LocationContextProvider';
